import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAdminUsers, impersonateUser, getAdminPlans } from '../../api/adminApi';
import { fmtIST } from '../../utils/dateFormat';

const cyan = '#00e5ff';

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
};

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [inputVal, setInputVal] = useState('');
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(null);
  const [emailVerified, setEmailVerified] = useState('all');
  const [upgraded, setUpgraded] = useState('all');
  const [latestSlot, setLatestSlot] = useState('all');
  const [plans, setPlans] = useState([]);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // Load plans for dropdown
  useEffect(() => {
    getAdminPlans()
      .then((res) => setPlans(res.data.plans || []))
      .catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    getAdminUsers({ search, page, limit: 20, emailVerified: emailVerified !== 'all' ? emailVerified : undefined, upgraded: upgraded !== 'all' ? upgraded : undefined, planId: latestSlot !== 'all' ? latestSlot : undefined })
      .then((res) => { setUsers(res.data.users); setTotal(res.data.total); setPages(res.data.pages); })
      .finally(() => setLoading(false));
  }, [search, page, emailVerified, upgraded, latestSlot]);

  useEffect(() => { load(); }, [load]);

  const handleImpersonate = async (e, userId) => {
    e.stopPropagation();
    setImpersonating(userId);
    try {
      const res = await impersonateUser(userId);
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('impersonating', JSON.stringify({ name: res.data.user.name, userCode: res.data.user.userCode }));
      window.location.href = '/dashboard';
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to impersonate');
      setImpersonating(null);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(inputVal);
  };

  return (
    <div>
      <h4 style={{ color: '#fff', fontWeight: 700, marginBottom: 4 }}>Users</h4>
      <div style={{ color: '#555', fontSize: 13, marginBottom: 24 }}>{total} total users</div>

      {/* Filters */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 20 }}>
        {/* Email Verified Filter */}
        <div>
          <label style={{ display: 'block', color: '#888', fontSize: 11, fontWeight: 600, marginBottom: 6 }}>Email Verified</label>
          <select
            value={emailVerified}
            onChange={(e) => { setEmailVerified(e.target.value); setPage(1); }}
            style={{
              width: '100%',
              background: '#1a1a1a',
              border: '1px solid rgba(0,229,255,0.2)',
              borderRadius: 8,
              padding: '8px 12px',
              color: '#fff',
              fontSize: 13,
              outline: 'none',
              cursor: 'pointer',
            }}
          >
            <option value="all" style={{ background: '#1a1a1a', color: '#fff' }}>All</option>
            <option value="true" style={{ background: '#1a1a1a', color: '#66ff99' }}>Yes</option>
            <option value="false" style={{ background: '#1a1a1a', color: '#ff6666' }}>No</option>
          </select>
        </div>

        {/* Upgraded Filter */}
        <div>
          <label style={{ display: 'block', color: '#888', fontSize: 11, fontWeight: 600, marginBottom: 6 }}>Upgraded</label>
          <select
            value={upgraded}
            onChange={(e) => { setUpgraded(e.target.value); setPage(1); }}
            style={{
              width: '100%',
              background: '#1a1a1a',
              border: '1px solid rgba(0,229,255,0.2)',
              borderRadius: 8,
              padding: '8px 12px',
              color: '#fff',
              fontSize: 13,
              outline: 'none',
              cursor: 'pointer',
            }}
          >
            <option value="all" style={{ background: '#1a1a1a', color: '#fff' }}>All</option>
            <option value="true" style={{ background: '#1a1a1a', color: '#66ff99' }}>Yes (with Slots)</option>
            <option value="false" style={{ background: '#1a1a1a', color: '#ff6666' }}>No (no Slots)</option>
          </select>
        </div>

        {/* Latest Slot Filter */}
        <div>
          <label style={{ display: 'block', color: '#888', fontSize: 11, fontWeight: 600, marginBottom: 6 }}>Latest Slot</label>
          <select
            value={latestSlot}
            onChange={(e) => { setLatestSlot(e.target.value); setPage(1); }}
            style={{
              width: '100%',
              background: '#1a1a1a',
              border: '1px solid rgba(0,229,255,0.2)',
              borderRadius: 8,
              padding: '8px 12px',
              color: '#fff',
              fontSize: 13,
              outline: 'none',
              cursor: 'pointer',
            }}
          >
            <option value="all" style={{ background: '#1a1a1a', color: '#fff' }}>All Plans</option>
            {plans.map((p) => (
              <option key={p._id} value={p._id} style={{ background: '#1a1a1a', color: '#00e5ff' }}>
                {p.planName}
              </option>
            ))}
          </select>
        </div>

        {/* Clear Filters Button */}
        {(emailVerified !== 'all' || upgraded !== 'all' || latestSlot !== 'all') && (
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button
              onClick={() => { setEmailVerified('all'); setUpgraded('all'); setLatestSlot('all'); setPage(1); }}
              style={{ width: '100%', background: 'rgba(255,255,255,0.06)', border: 'none', color: '#888', borderRadius: 8, padding: '8px 12px', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
        <input
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          placeholder={isMobile ? "Search..." : "Search by name, email, code, mobile..."}
          style={{ flex: 1, minWidth: isMobile ? '100%' : 'auto', background: 'rgba(0,229,255,0.04)', border: '1px solid rgba(0,229,255,0.2)', borderRadius: 8, padding: '9px 14px', color: '#fff', fontSize: 13, outline: 'none' }}
        />
        <button type="submit" style={{ background: cyan, border: 'none', color: '#000', borderRadius: 8, padding: '9px 20px', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          Search
        </button>
        {search && (
          <button type="button" onClick={() => { setInputVal(''); setSearch(''); setPage(1); }}
            style={{ background: 'rgba(255,255,255,0.06)', border: 'none', color: '#888', borderRadius: 8, padding: '9px 14px', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            Clear
          </button>
        )}
      </form>

      {/* Table / Cards */}
      {isMobile ? (
        /* Mobile Card View */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {loading ? (
            <div style={{ color: '#555', textAlign: 'center', padding: 32, fontSize: 13 }}>Loading...</div>
          ) : users.length === 0 ? (
            <div style={{ color: '#555', textAlign: 'center', padding: 32, fontSize: 13 }}>No users found.</div>
          ) : (
            users.map((u) => (
              <div
                key={u._id}
                onClick={() => navigate(`/admin/users/${u._id}`)}
                style={{ background: '#111', border: '1px solid rgba(0,229,255,0.1)', borderRadius: 12, padding: '16px', cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 12, gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: '#fff', fontSize: 14, fontWeight: 600, marginBottom: 2 }}>{u.name}</div>
                    <div style={{ color: cyan, fontSize: 11, fontFamily: 'monospace' }}>{u.userCode}</div>
                  </div>
                  <span style={{ background: u.isActive ? 'rgba(102,255,153,0.12)' : 'rgba(200,50,50,0.12)', color: u.isActive ? '#66ff99' : '#ff6666', border: `1px solid ${u.isActive ? 'rgba(102,255,153,0.3)' : 'rgba(200,50,50,0.3)'}`, borderRadius: 4, padding: '2px 6px', fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {u.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12, fontSize: 12 }}>
                  <div>
                    <div style={{ color: '#555', fontSize: 10 }}>Slot</div>
                    <div style={{ color: '#66ff99' }}>{u.planId?.planName || '—'}</div>
                  </div>
                  <div>
                    <div style={{ color: '#555', fontSize: 10 }}>Upline</div>
                    <div style={{ color: '#888', fontFamily: 'monospace', fontSize: 12 }}>{u.referredBy?.userCode || '—'}</div>
                    {u.referredBy?.name && <div style={{ color: '#666', fontSize: 10 }}>{u.referredBy.name}</div>}
                  </div>
                </div>
                <div style={{ color: '#555', fontSize: 11, marginBottom: 12 }}>Joined: {fmtIST(u.createdAt)}</div>
                <button
                  onClick={(e) => { e.stopPropagation(); handleImpersonate(e, u._id); }}
                  disabled={impersonating === u._id}
                  style={{ width: '100%', background: 'rgba(0,229,255,0.08)', border: '1px solid rgba(0,229,255,0.25)', color: '#00e5ff', borderRadius: 6, padding: '6px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                >
                  {impersonating === u._id ? '...' : 'Login as'}
                </button>
              </div>
            ))
          )}
        </div>
      ) : (
        /* Desktop Table View */
        <div style={{ background: '#111', border: '1px solid rgba(0,229,255,0.1)', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'rgba(0,229,255,0.07)', borderBottom: '1px solid rgba(0,229,255,0.12)' }}>
                {['#', 'Name', 'Code', 'Upline Code', 'Active Slot', 'Joined', 'Status', ''].map((h) => (
                  <th key={h} style={{ color: cyan, fontSize: 12, fontWeight: 600, padding: '12px 14px', textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ color: '#555', textAlign: 'center', padding: 32, fontSize: 13 }}>Loading...</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={8} style={{ color: '#555', textAlign: 'center', padding: 32, fontSize: 13 }}>No users found.</td></tr>
              ) : users.map((u, i) => (
                <tr key={u._id}
                  onClick={() => navigate(`/admin/users/${u._id}`)}
                  style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', cursor: 'pointer', transition: 'background 0.15s' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,229,255,0.03)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={{ color: '#555', fontSize: 12, padding: '11px 14px' }}>{(page - 1) * 20 + i + 1}</td>
                  <td style={{ color: '#fff', fontSize: 13, fontWeight: 500, padding: '11px 14px', whiteSpace: 'nowrap' }}>{u.name}</td>
                  <td style={{ color: cyan, fontSize: 12, padding: '11px 14px', fontFamily: 'monospace' }}>{u.userCode}</td>
                  <td style={{ padding: '11px 14px' }}>
                    <div style={{ color: '#888', fontSize: 12, fontFamily: 'monospace' }}>{u.referredBy?.userCode || '—'}</div>
                    {u.referredBy?.name && <div style={{ color: '#555', fontSize: 11 }}>{u.referredBy.name}</div>}
                  </td>
                  <td style={{ color: '#66ff99', fontSize: 12, padding: '11px 14px' }}>{u.planId?.planName || '—'}</td>
                  <td style={{ color: '#555', fontSize: 11, padding: '11px 14px', whiteSpace: 'nowrap' }}>{fmtIST(u.createdAt)}</td>
                  <td style={{ padding: '11px 14px' }}>
                    <span style={{ background: u.isActive ? 'rgba(102,255,153,0.12)' : 'rgba(200,50,50,0.12)', color: u.isActive ? '#66ff99' : '#ff6666', border: `1px solid ${u.isActive ? 'rgba(102,255,153,0.3)' : 'rgba(200,50,50,0.3)'}`, borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                      {u.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '11px 14px' }} onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={(e) => handleImpersonate(e, u._id)}
                      disabled={impersonating === u._id}
                      style={{ background: 'rgba(0,229,255,0.08)', border: '1px solid rgba(0,229,255,0.25)', color: '#00e5ff', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                    >
                      {impersonating === u._id ? '...' : 'Login as'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20, flexWrap: 'wrap' }}>
          {Array.from({ length: Math.min(pages, isMobile ? 5 : pages) }, (_, i) => i + 1).map((p) => (
            <button key={p} onClick={() => setPage(p)}
              style={{ background: p === page ? cyan : 'rgba(255,255,255,0.06)', border: 'none', color: p === page ? '#000' : '#888', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              {p}
            </button>
          ))}
          {isMobile && pages > 5 && <span style={{ color: '#555', fontSize: 12, padding: '6px 12px' }}>... {pages}</span>}
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
